/**
 * conversationWorkflowsApi — thin client for the multi-workflow endpoints under
 * /api/bot/{botID}/conversation. Mirrors the helper style in WorkflowEditor
 * (auth headers passed in, JSON in/out). Kept in its own module so both the
 * WorkflowList view and the canvas can share types without a circular import.
 */
import type { BotConversationGraph, GraphFinding } from './WorkflowEditor';

type AuthHdrs = Record<string, string>;

export interface ConversationWorkflowMeta {
  id: string;
  bot_id: string;
  name: string;
  description?: string;
  template_id?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  head_version: number;
  node_count: number;
  active: boolean;
}

export interface ConversationTemplate {
  id: string;
  name: string;
  description: string;
  category?: string;
  node_count?: number;
}

export const MAIN_WORKFLOW_NAME = 'Main';

function isMainWorkflowName(name: string): boolean {
  return name.trim().toLowerCase() === MAIN_WORKFLOW_NAME.toLowerCase();
}

async function jsonOrNull(r: Response): Promise<unknown> {
  const text = await r.text();
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return null;
  }
}

function base(botID: string): string {
  return `/api/bot/${encodeURIComponent(botID)}/conversation`;
}

export async function listWorkflows(
  botID: string,
  auth: AuthHdrs,
): Promise<{ workflows: ConversationWorkflowMeta[]; active_id: string }> {
  const r = await fetch(`${base(botID)}/workflows`, { headers: auth });
  if (!r.ok) throw new Error(`list workflows: HTTP ${r.status}`);
  const data = (await jsonOrNull(r)) as { workflows?: ConversationWorkflowMeta[]; active_id?: string } | null;
  return { workflows: data?.workflows || [], active_id: data?.active_id || '' };
}

export async function listTemplates(botID: string, auth: AuthHdrs): Promise<ConversationTemplate[]> {
  const r = await fetch(`${base(botID)}/templates`, { headers: auth });
  if (!r.ok) throw new Error(`list templates: HTTP ${r.status}`);
  const data = (await jsonOrNull(r)) as { templates?: ConversationTemplate[] } | null;
  return data?.templates || [];
}

export async function createWorkflow(
  botID: string,
  auth: AuthHdrs,
  body: { name: string; description?: string; template_id?: string },
): Promise<{ workflow: ConversationWorkflowMeta; graph: BotConversationGraph | null }> {
  const r = await fetch(`${base(botID)}/workflows`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...auth },
    body: JSON.stringify(body),
  });
  const data = (await jsonOrNull(r)) as
    | { workflow?: ConversationWorkflowMeta; graph?: BotConversationGraph | null; error?: string }
    | null;
  if (!r.ok || !data?.workflow) {
    throw new Error(data?.error || `create workflow: HTTP ${r.status}`);
  }
  return { workflow: data.workflow, graph: data.graph || null };
}

export async function ensureMainWorkflow(
  botID: string,
  auth: AuthHdrs,
): Promise<ConversationWorkflowMeta> {
  const r = await fetch(`${base(botID)}/workflows/ensure-main`, {
    method: 'POST',
    headers: auth,
  });
  const data = (await jsonOrNull(r)) as { workflow?: ConversationWorkflowMeta; error?: string } | null;
  if (!r.ok || !data?.workflow) {
    throw new Error(data?.error || `ensure main workflow: HTTP ${r.status}`);
  }
  const workflow = data.workflow;
  if (!isMainWorkflowName(workflow.name)) {
    throw new Error(`ensure main workflow: expected ${MAIN_WORKFLOW_NAME}, got ${workflow.name}`);
  }
  return workflow;
}

/**
 * Writes a new version of a workflow's graph — i.e. PUBLISHES it. The editor
 * stages edits as unpublished drafts (see BotConfigContext's GraphDraftStore);
 * this is what Configure's "Publish changes" calls to make them live.
 *
 * Returns the new server-side version so the canvas's status chip can show it.
 */
export async function saveWorkflowGraph(
  botID: string,
  wfID: string,
  auth: AuthHdrs,
  graph: BotConversationGraph,
): Promise<{ version: number; findings: GraphFinding[] }> {
  const r = await fetch(`${base(botID)}/workflows/${encodeURIComponent(wfID)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...auth },
    body: JSON.stringify(graph),
  });
  const data = (await jsonOrNull(r)) as
    | { version?: number; findings?: GraphFinding[]; error?: string }
    | null;
  if (!r.ok) {
    throw new Error(
      graphWriteErrorMessage(graph, data?.findings || [], data?.error || `HTTP ${r.status}`),
    );
  }
  return { version: data?.version || 0, findings: data?.findings || [] };
}

/**
 * Dry-run validation (`POST /conversation/validate`) — run-mode findings, and
 * nothing is persisted. Publish uses this as a pre-flight so a graph with
 * blocking problems is refused before anything is written.
 */
export async function validateWorkflowGraph(
  botID: string,
  auth: AuthHdrs,
  graph: BotConversationGraph,
): Promise<{ findings: GraphFinding[]; has_blocking: boolean }> {
  const r = await fetch(`${base(botID)}/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...auth },
    body: JSON.stringify(graph),
  });
  const data = (await jsonOrNull(r)) as
    | { findings?: GraphFinding[]; has_blocking?: boolean; error?: string }
    | null;
  if (!r.ok) throw new Error(data?.error || `validate workflow graph: HTTP ${r.status}`);
  return { findings: data?.findings || [], has_blocking: Boolean(data?.has_blocking) };
}

/**
 * Publishes a set of unpublished graph drafts — what Configure's "Publish
 * changes" calls for the dirty subset of the draft store.
 *
 * EVERY graph is validated before ANY is written, so a batch with one broken
 * draft writes nothing at all rather than leaving half the workflows live and
 * half not. Throws the first blocking finding's message.
 *
 * Returns the new server version per workflow id.
 */
export async function publishWorkflowGraphs(
  botID: string,
  auth: AuthHdrs,
  drafts: Array<{ workflowID: string; graph: BotConversationGraph }>,
): Promise<Record<string, number>> {
  for (const { graph } of drafts) {
    const { findings, has_blocking } = await validateWorkflowGraph(botID, auth, graph);
    if (has_blocking) {
      const blocking = findings.find((f) => f.kind === 'error');
      throw new Error(
        blocking?.message || 'The workflow has problems that must be fixed before publishing.',
      );
    }
  }
  const versions: Record<string, number> = {};
  for (const { workflowID, graph } of drafts) {
    const { version } = await saveWorkflowGraph(botID, workflowID, auth, graph);
    versions[workflowID] = version;
  }
  return versions;
}

/** Turn a rejected graph write into one concise, human reason. */
export function graphWriteErrorMessage(
  graph: BotConversationGraph,
  findings: GraphFinding[],
  fallback: string,
): string {
  if (!graph.nodes.length) {
    return 'An empty graph can’t be published — add or describe a step first.';
  }
  const blocking = findings.find((f) => f.kind === 'error');
  if (blocking) return blocking.message;
  return fallback || 'Could not publish the workflow.';
}

export async function activateWorkflow(botID: string, wfID: string, auth: AuthHdrs): Promise<void> {
  const r = await fetch(`${base(botID)}/workflows/${encodeURIComponent(wfID)}/activate`, {
    method: 'POST',
    headers: auth,
  });
  if (!r.ok) {
    const data = (await jsonOrNull(r)) as { error?: string } | null;
    throw new Error(data?.error || `activate workflow: HTTP ${r.status}`);
  }
}

export async function renameWorkflow(
  botID: string,
  wfID: string,
  auth: AuthHdrs,
  body: { name?: string; description?: string },
): Promise<ConversationWorkflowMeta> {
  const r = await fetch(`${base(botID)}/workflows/${encodeURIComponent(wfID)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...auth },
    body: JSON.stringify(body),
  });
  const data = (await jsonOrNull(r)) as { workflow?: ConversationWorkflowMeta; error?: string } | null;
  if (!r.ok || !data?.workflow) {
    throw new Error(data?.error || `rename workflow: HTTP ${r.status}`);
  }
  return data.workflow;
}

export async function deleteWorkflow(botID: string, wfID: string, auth: AuthHdrs): Promise<void> {
  const r = await fetch(`${base(botID)}/workflows/${encodeURIComponent(wfID)}`, {
    method: 'DELETE',
    headers: auth,
  });
  if (!r.ok && r.status !== 204) {
    const data = (await jsonOrNull(r)) as { error?: string } | null;
    throw new Error(data?.error || `delete workflow: HTTP ${r.status}`);
  }
}
